import { ArrowUpRight } from "lucide-react";
import { Reveal } from "./Reveal";

const RATES = [
  { title: "Keynote Address", duration: "30–60 minutes", cost: "$3,500 – $7,500 AUD", desc: "Tailored insights into industry trends, opportunities and future scenarios." },
  { title: "Panel Contribution", duration: "30–60 minutes", cost: "$1,500 – $3,000 AUD", desc: "Expert commentary, practical project examples and audience engagement." },
  { title: "Workshops & Masterclasses", duration: "Half or full day", cost: "$5,000 – $10,000 AUD", desc: "Interactive facilitation and tailored learning design in a specialised subject area." },
  { title: "Council & Community Sessions", duration: "Half or full day", cost: "$3,000 – $5,000 AUD", desc: "Tailored engagement and workshops for a specific topic or project. Subsidised by agreement." },
];

const UPCOMING = [
  {
    name: "Queensland Energy at a Turning Point",
    date: "8 October",
    meta: "Tickets via Eventbrite",
    url: "https://www.eventbrite.com.au/e/queensland-energy-at-a-turning-point-tickets-2000908631580?aff=oddtdtcreator",
  },
  {
    name: "Hub Forum 2026",
    date: "29 September",
    meta: "Decarb Hub",
    url: "https://decarb-hub.org/events/hub-forum-2026",
  },
];

const PHOTOS = [
  { src: "/photos/rachelle-speaking-1.webp", alt: "Rachelle Cooper Kulkarni speaking on stage" },
  { src: "/photos/rachelle-speaking-3.jpg", alt: "Rachelle Cooper Kulkarni presenting with a microphone" },
  { src: "/photos/rachelle-speaking-4.webp", alt: "Rachelle Cooper Kulkarni at an energy industry event" },
];

export const Speaking = () => (
  <section id="speaking" data-testid="speaking-section" className="bg-cream text-ink py-28 md:py-36">
    <div className="max-w-7xl mx-auto px-6 md:px-10">
      <Reveal>
        <p className="eyebrow text-royal">Speaking & Facilitation</p>
        <h2 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl leading-tight mt-6 max-w-3xl">
          Conversations that <span className="italic text-royal">shift rooms</span>
        </h2>
        <p className="mt-8 max-w-2xl text-ink/70 text-base leading-relaxed">
          For more than a decade Rachelle has designed and delivered keynotes, panels, workshops
          and dialogues across industry and sector boundaries — from All-Energy and National
          Energy Week to First Nations and the Energy Transition.
        </p>
      </Reveal>

      <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-ink/10 border border-ink/10">
        {RATES.map((r, i) => (
          <Reveal key={r.title} delay={i * 0.06} className="bg-cream">
            <div className="p-8 h-full flex flex-col" data-testid={`rate-card-${i}`}>
              <h3 className="font-display text-xl">{r.title}</h3>
              <p className="eyebrow text-ink/50 mt-2">{r.duration}</p>
              <p className="mt-6 text-sm text-ink/70 leading-relaxed flex-1">{r.desc}</p>
              <p className="font-display text-royal text-lg mt-6">{r.cost}</p>
            </div>
          </Reveal>
        ))}
      </div>
      <Reveal delay={0.1}>
        <p className="mt-4 text-ink/50 text-xs">Fees include preparation and standard materials. Travel and accommodation additional if required. All packages adaptable to bespoke requirements.</p>
      </Reveal>

      <Reveal delay={0.08}>
        <div className="mt-20 grid sm:grid-cols-3 gap-8" data-testid="speaking-photos">
          {PHOTOS.map((p, i) => (
            <div key={p.src} className={`relative ${i === 1 ? "sm:mt-12" : ""}`}>
              <div className="absolute inset-0 translate-x-3 translate-y-3 border border-royal/40" aria-hidden="true" />
              <img src={p.src} alt={p.alt} data-testid={`speaking-photo-${i}`}
                className="relative w-full aspect-[3/4] object-cover" />
            </div>
          ))}
        </div>
      </Reveal>

      <div className="mt-20 grid md:grid-cols-2 gap-16">
        <Reveal>
          <h3 className="eyebrow text-royal">Upcoming stages</h3>
          <div className="mt-6">
            {UPCOMING.map((e, i) => (
              <a key={e.name} href={e.url} target="_blank" rel="noopener noreferrer"
                data-testid={`upcoming-event-${i}`}
                className="group flex items-center justify-between gap-4 border-b border-ink/10 py-4 hover:bg-royal/5 transition-colors duration-300 px-2 -mx-2">
                <div className="flex items-baseline gap-5">
                  <span className="font-display text-royal text-xl whitespace-nowrap">{e.date}</span>
                  <div>
                    <p className="text-sm text-ink font-medium">{e.name}</p>
                    <p className="text-xs text-ink/50 mt-0.5">{e.meta}</p>
                  </div>
                </div>
                <ArrowUpRight size={18} className="text-royal shrink-0 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
              </a>
            ))}
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          <h3 className="eyebrow text-royal">What organisers say</h3>
          <blockquote className="mt-6 font-display italic text-xl leading-relaxed text-ink/90" data-testid="speaking-testimonial">
            "This year was a great success, marked by insightful conversations and impactful
            collaboration among speakers and attendees. Thank you for all the effort you put
            into preparing for, chairing and attending the conference."
          </blockquote>
          <p className="mt-4 text-sm text-ink/60">Aya Mersal — Conference Producer, Informa Connect · First Nations and the Energy Transition Forum 2025</p>
          <a href="#enquire" data-testid="speaking-book-btn"
            className="mt-10 inline-flex items-center gap-2 bg-royal text-cream eyebrow px-6 py-3.5 rounded-full hover:bg-ink transition-colors duration-300">
            Book Rachelle to speak <ArrowUpRight size={14} />
          </a>
        </Reveal>
      </div>
    </div>
  </section>
);
